import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Rocket,
  CheckCircle,
  Globe,
  DownloadSimple,
  ArrowRight,
  Copy,
  CloudArrowUp,
  Link as LinkIcon,
  ShieldCheck,
  MagnifyingGlass,
  ChartLineUp,
} from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LaunchKit() {
  const [searchParams] = useSearchParams();
  const [websites, setWebsites] = useState([]);
  const [selectedId, setSelectedId] = useState(searchParams.get("websiteId") || "");
  const [launchData, setLaunchData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeHost, setActiveHost] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/websites`).then((res) => {
      const sites = (res.data.websites || []).filter((s) => s.status === "ready" || s.status === "launched");
      setWebsites(sites);
      if (!selectedId && sites.length > 0) {
        setSelectedId(sites[0].id);
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    axios
      .get(`${API}/websites/${selectedId}/launch-kit`)
      .then((res) => {
        setLaunchData(res.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedId]);

  const handleDownload = async () => {
    if (!selectedId) return;
    try {
      const res = await axios.get(`${API}/websites/${selectedId}`);
      const html = res.data.html_content || "";
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${res.data.business_name?.replace(/\s+/g, "_").toLowerCase() || "website"}.html`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Website downloaded!");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const copyStep = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (websites.length === 0 && !loading) {
    return (
      <div data-testid="launch-kit-page" className="space-y-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Launch Kit
          </h1>
          <p className="text-sm text-[#52525B] mt-1">Generate a website first, then come back to launch it.</p>
        </div>
        <div className="bg-white border border-border p-12 text-center">
          <Rocket weight="thin" size={48} className="mx-auto mb-3 text-[#A1A1AA]" />
          <p className="text-sm text-[#52525B]">No websites ready to launch</p>
          <button onClick={() => navigate("/generator")} className="mt-3 text-xs font-semibold text-[#002FA7] hover:underline">
            Generate a website first
          </button>
        </div>
      </div>
    );
  }

  const hostIcons = [CloudArrowUp, CloudArrowUp, Globe, Globe];

  return (
    <div data-testid="launch-kit-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Launch Kit
          </h1>
          <p className="text-sm text-[#52525B] mt-1">
            Step-by-step guide to get your client's website live in minutes.
          </p>
        </div>
        <button
          data-testid="download-website-btn"
          onClick={handleDownload}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002482] transition-colors"
        >
          <DownloadSimple weight="bold" size={16} />
          Download HTML
        </button>
      </div>

      {/* Website Selector */}
      <div className="bg-white border border-border p-4 flex items-center gap-4">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B]">Website:</span>
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger data-testid="website-selector" className="rounded-none flex-1 max-w-md">
            <SelectValue placeholder="Select a website" />
          </SelectTrigger>
          <SelectContent>
            {websites.map((site) => (
              <SelectItem key={site.id} value={site.id}>
                {site.business_name} — {site.industry}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="spinner" /></div>
      ) : launchData ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Hosting Options */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-border">
              <div className="p-4 border-b border-border">
                <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                  Hosting Options
                </h3>
                <p className="text-xs text-[#52525B] mt-1">Pick the best option for your client</p>
              </div>

              {/* Hosting tabs */}
              <div className="flex border-b border-border">
                {launchData.hosting_options?.map((host, i) => (
                  <button
                    key={i}
                    data-testid={`hosting-tab-${i}`}
                    onClick={() => setActiveHost(i)}
                    className={`flex-1 px-4 py-3 text-xs font-semibold text-center transition-colors border-b-2 ${
                      activeHost === i
                        ? "border-[#002FA7] text-[#002FA7] bg-[#EEF2FF]"
                        : "border-transparent text-[#52525B] hover:bg-[#F5F5F7]"
                    }`}
                  >
                    {host.name}
                    <span className={`block text-[10px] font-normal mt-0.5 ${
                      host.type === "free" ? "text-[#00C853]" : "text-[#FF9100]"
                    }`}>
                      {host.cost}
                    </span>
                  </button>
                ))}
              </div>

              {/* Steps */}
              {launchData.hosting_options?.[activeHost] && (
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-bold text-[#002FA7]">
                      Estimated time: {launchData.hosting_options[activeHost].estimated_time}
                    </span>
                  </div>
                  <ol className="space-y-3">
                    {launchData.hosting_options[activeHost].steps.map((step, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-3 group animate-fade-in-up"
                        style={{ animationDelay: `${j * 0.05}s` }}
                      >
                        <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-[#F5F5F7] border border-border text-xs font-bold text-[#52525B]">
                          {j + 1}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm">{step}</p>
                        </div>
                        <button
                          onClick={() => copyStep(step)}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#F5F5F7] transition-all"
                          title="Copy step"
                        >
                          <Copy size={12} className="text-[#52525B]" />
                        </button>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>

            {/* Domain Options */}
            <div className="bg-white border border-border p-5">
              <h3 className="text-base font-bold tracking-tight mb-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                <LinkIcon weight="bold" size={16} className="inline mr-2 text-[#002FA7]" />
                Domain Registration
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {launchData.domain_options?.map((domain, i) => (
                  <a
                    key={i}
                    href={`https://${domain.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`domain-option-${i}`}
                    className="border border-border p-3 hover:border-[#002FA7] transition-colors group"
                  >
                    <div className="text-sm font-bold">{domain.provider}</div>
                    <div className="text-xs text-[#52525B] mt-0.5">{domain.cost}</div>
                    <div className="text-[10px] text-[#002FA7] font-mono mt-1 group-hover:underline">
                      {domain.url}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Checklist & SEO Tips */}
          <div className="space-y-4">
            <div className="bg-white border border-border p-5">
              <h3 className="text-base font-bold tracking-tight mb-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                <CheckCircle weight="bold" size={16} className="inline mr-2 text-[#00C853]" />
                Go-Live Checklist
              </h3>
              <ul className="space-y-2">
                {launchData.go_live_checklist?.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      data-testid={`checklist-${i}`}
                      className="mt-1 accent-[#002FA7]"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white border border-border p-5">
              <h3 className="text-base font-bold tracking-tight mb-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                <ChartLineUp weight="bold" size={16} className="inline mr-2 text-[#002FA7]" />
                SEO Tips
              </h3>
              <ul className="space-y-2">
                {launchData.seo_tips?.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <ArrowRight weight="bold" size={12} className="mt-1 text-[#002FA7] flex-shrink-0" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
